import { useState, useRef, useCallback } from "react";

// ============================================================
// StressTest → Proxy FormAssembly
//
// Dispara N "usuarios virtuales" (workers) en paralelo. Cada
// worker envía requests en serie (como haría un operador real)
// hasta cumplir la duración. Mide éxito/error y latencias.
//
// ⚠️ USAR CONTRA UN FORM DE PRUEBA, nunca contra el form real:
//    cada request exitoso crea una response en FormAssembly.
//
// Límites de probar desde un navegador:
//  - El browser limita conexiones simultáneas por host (~6 en
//    HTTP/1.1). Con más workers que eso, los requests se encolan.
//  - Para cargas serias (50+ VUs) usá k6 o similar desde consola.
//    Este componente sirve para validar el rango realista de un
//    evento (1-20 VUs) y detectar errores intermitentes.
// ============================================================

const DEFAULTS = {
  proxyUrl: "http://localhost:5132/api/FormAssemblyProxy/submit",
  formId: "5228366", // ← poné acá tu form de PRUEBA
  idAE: "a14Pn00000Evs21IAB",
  workers: 5,        // usuarios virtuales concurrentes
  durationSec: 60,   // duración de la prueba
  thinkTimeMs: 500,  // pausa entre requests de un mismo worker
};

const pct = (sorted, p) => {
  if (!sorted.length) return 0;
  const i = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, i)];
};

export default function StressTest() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);

  const stopRef = useRef(false);
  const dataRef = useRef(null);

  const set = (k) => (e) =>
    setCfg((c) => ({ ...c, [k]: e.target.value }));

  // ---------- un request de check-in ----------
  const oneRequest = useCallback(async () => {
    const d = dataRef.current;
    const documento = String(10000000 + Math.floor(Math.random() * 89999999));
    const body = new URLSearchParams();
    body.set("tfa_1", documento);
    body.set("tfa_3", cfg.idAE);

    const t0 = performance.now();
    try {
      const res = await fetch(
        `${cfg.proxyUrl.replace(/\/$/, "")}/${encodeURIComponent(cfg.formId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        }
      );
      const ms = performance.now() - t0;
      let payload = null;
      try {
        payload = await res.json();
      } catch (_) {}

      d.latencies.push(ms);
      if (res.ok && payload?.success) {
        d.ok++;
      } else {
        d.fail++;
        d.errors.push(
          `HTTP ${res.status} — ${payload?.message || "sin mensaje"} (${Math.round(ms)}ms)`
        );
      }
    } catch (err) {
      const ms = performance.now() - t0;
      d.fail++;
      d.errors.push(`Red: ${err.message} (${Math.round(ms)}ms)`);
    }
  }, [cfg]);

  // ---------- refrescar métricas en pantalla ----------
  const refresh = useCallback((final = false) => {
    const d = dataRef.current;
    const elapsed = (performance.now() - d.start) / 1000;
    const sorted = [...d.latencies].sort((a, b) => a - b);
    setStats({
      elapsed: Math.min(elapsed, Number(dataRef.current.durationSec)),
      total: d.ok + d.fail,
      ok: d.ok,
      fail: d.fail,
      rps: elapsed > 0 ? (d.ok + d.fail) / elapsed : 0,
      avg: sorted.length ? sorted.reduce((a, b) => a + b, 0) / sorted.length : 0,
      p50: pct(sorted, 50),
      p95: pct(sorted, 95),
      max: sorted.length ? sorted[sorted.length - 1] : 0,
      final,
    });
    setErrors(d.errors.slice(-8)); // últimos 8 errores
  }, []);

  // ---------- correr la prueba ----------
  const start = useCallback(async () => {
    stopRef.current = false;
    dataRef.current = {
      start: performance.now(),
      durationSec: cfg.durationSec,
      ok: 0,
      fail: 0,
      latencies: [],
      errors: [],
    };
    setStats(null);
    setErrors([]);
    setRunning(true);

    const endAt = performance.now() + Number(cfg.durationSec) * 1000;
    const think = Math.max(0, Number(cfg.thinkTimeMs));

    // cada worker es un loop secuencial (simula un operador escaneando)
    const worker = async () => {
      while (!stopRef.current && performance.now() < endAt) {
        await oneRequest();
        if (think > 0)
          await new Promise((r) => setTimeout(r, think * (0.5 + Math.random())));
      }
    };

    const ticker = setInterval(() => refresh(false), 500);
    await Promise.all(
      Array.from({ length: Math.max(1, Number(cfg.workers)) }, worker)
    );
    clearInterval(ticker);
    refresh(true);
    setRunning(false);
  }, [cfg, oneRequest, refresh]);

  const stop = () => {
    stopRef.current = true;
  };

  // ============================ UI ============================
  const S = styles;
  const errRate = stats && stats.total > 0 ? (stats.fail / stats.total) * 100 : 0;

  return (
    <div style={S.wrapper}>
      <div style={S.card}>
        <p style={S.eyebrow}>Prueba de estrés</p>
        <h2 style={S.title}>Proxy FormAssembly</h2>

        <div style={S.warning}>
          ⚠️ Apuntá a un <strong>form de prueba</strong>: cada request exitoso
          crea una response real en FormAssembly.
        </div>

        {/* Configuración */}
        <div style={S.grid}>
          <label style={S.field}>
            <span style={S.fieldLabel}>URL del proxy (sin formId)</span>
            <input style={S.input} value={cfg.proxyUrl} onChange={set("proxyUrl")} disabled={running} />
          </label>
          <label style={S.field}>
            <span style={S.fieldLabel}>Form ID (de prueba)</span>
            <input style={S.input} value={cfg.formId} onChange={set("formId")} disabled={running} />
          </label>
          <label style={S.field}>
            <span style={S.fieldLabel}>ID AE (tfa_3)</span>
            <input style={S.input} value={cfg.idAE} onChange={set("idAE")} disabled={running} />
          </label>
          <div style={S.row3}>
            <label style={S.field}>
              <span style={S.fieldLabel}>Workers</span>
              <input style={S.input} type="number" min="1" max="30" value={cfg.workers} onChange={set("workers")} disabled={running} />
            </label>
            <label style={S.field}>
              <span style={S.fieldLabel}>Duración (s)</span>
              <input style={S.input} type="number" min="5" max="600" value={cfg.durationSec} onChange={set("durationSec")} disabled={running} />
            </label>
            <label style={S.field}>
              <span style={S.fieldLabel}>Pausa (ms)</span>
              <input style={S.input} type="number" min="0" max="5000" value={cfg.thinkTimeMs} onChange={set("thinkTimeMs")} disabled={running} />
            </label>
          </div>
        </div>

        {/* Métricas */}
        {stats && (
          <>
            <div style={S.metrics}>
              <Metric label="Requests" value={stats.total} />
              <Metric label="OK" value={stats.ok} color={PALETTE.okText} />
              <Metric
                label="Errores"
                value={`${stats.fail} (${errRate.toFixed(1)}%)`}
                color={stats.fail > 0 ? PALETTE.error : undefined}
              />
              <Metric label="Req/s" value={stats.rps.toFixed(2)} />
              <Metric label="Lat. media" value={`${Math.round(stats.avg)} ms`} />
              <Metric label="p50" value={`${Math.round(stats.p50)} ms`} />
              <Metric
                label="p95"
                value={`${Math.round(stats.p95)} ms`}
                color={stats.p95 > 5000 ? PALETTE.error : undefined}
              />
              <Metric label="Máx" value={`${Math.round(stats.max)} ms`} />
            </div>

            {stats.final && (
              <div style={{ ...S.verdict, ...(stats.fail === 0 && stats.p95 < 5000 ? S.verdictOk : S.verdictBad) }}>
                {stats.fail === 0 && stats.p95 < 5000
                  ? "✓ Sin errores y p95 < 5s: el proxy aguanta esta carga."
                  : stats.fail > 0
                  ? `✕ Hubo ${stats.fail} errores (${errRate.toFixed(1)}%). Si son 502 intermitentes bajo concurrencia, revisá el CookieContainer compartido del HttpClient.`
                  : "△ Sin errores, pero p95 alto: la fila del evento va a sentir la espera."}
              </div>
            )}
          </>
        )}

        {/* Últimos errores */}
        {errors.length > 0 && (
          <div style={S.errorLog}>
            <span style={S.fieldLabel}>Últimos errores</span>
            {errors.map((e, i) => (
              <code key={i} style={S.errorLine}>{e}</code>
            ))}
          </div>
        )}

        {/* Acciones */}
        <div style={S.actions}>
          {running ? (
            <button style={S.btnSecondary} onClick={stop}>Detener</button>
          ) : (
            <button style={S.btnPrimary} onClick={start}>Iniciar prueba</button>
          )}
        </div>

        <p style={S.footnote}>
          Verificá al final que la cantidad de responses en el form de prueba
          coincida con los OK. Para cargas mayores a ~20 workers usá k6 desde
          consola: el navegador limita las conexiones simultáneas por host.
        </p>
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={styles.metric}>
      <span style={styles.metricLabel}>{label}</span>
      <span style={{ ...styles.metricValue, ...(color ? { color } : {}) }}>{value}</span>
    </div>
  );
}

// ---------- paleta ----------
const PALETTE = {
  teal: "#1B6B7C",
  turquoise: "#4AC3D6",
  lime: "#DCE02A",
  bg: "#FDFDFB",
  surface: "#FFFFFF",
  border: "#DDEBEE",
  textMuted: "#6E8C94",
  error: "#D65A4A",
  okText: "#3A8A4D",
  warnBg: "#FFF6E5",
  warnBorder: "#F0DCAE",
  warnText: "#8A6A1F",
};

// ---------- estilos ----------
const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: PALETTE.bg,
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 560,
    background: PALETTE.surface,
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 16,
    padding: 24,
    color: PALETTE.teal,
    boxShadow: "0 8px 24px rgba(27,107,124,0.10)",
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: PALETTE.turquoise,
    fontWeight: 700,
  },
  title: { margin: "6px 0 14px", fontSize: 22, fontWeight: 700 },
  warning: {
    background: PALETTE.warnBg,
    border: `1px solid ${PALETTE.warnBorder}`,
    color: PALETTE.warnText,
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
    marginBottom: 16,
  },
  grid: { display: "flex", flexDirection: "column", gap: 12 },
  row3: { display: "flex", gap: 10 },
  field: { display: "flex", flexDirection: "column", gap: 4, flex: 1 },
  fieldLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: PALETTE.textMuted,
    fontWeight: 700,
  },
  input: {
    padding: "9px 11px",
    fontSize: 14,
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 8,
    color: PALETTE.teal,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  metrics: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
  },
  metric: {
    background: "#F2FAFB",
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 10,
    padding: "10px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    alignItems: "center",
  },
  metricLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: PALETTE.textMuted,
    fontWeight: 700,
  },
  metricValue: { fontSize: 16, fontWeight: 700, color: PALETTE.teal },
  verdict: {
    marginTop: 14,
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 600,
  },
  verdictOk: {
    background: "#EDF8EF",
    border: "1px solid #BFE3C7",
    color: PALETTE.okText,
  },
  verdictBad: {
    background: "#FDF0EE",
    border: "1px solid #F0C7C0",
    color: PALETTE.error,
  },
  errorLog: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  errorLine: {
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    fontSize: 12,
    color: PALETTE.error,
    background: "#FDF0EE",
    borderRadius: 6,
    padding: "4px 8px",
    wordBreak: "break-all",
  },
  actions: { marginTop: 18, display: "flex", gap: 10 },
  btnPrimary: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 700,
    color: "#FFFFFF",
    background: PALETTE.teal,
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
  },
  btnSecondary: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 700,
    color: PALETTE.teal,
    background: "transparent",
    border: `2px solid ${PALETTE.turquoise}`,
    borderRadius: 999,
    cursor: "pointer",
  },
  footnote: {
    marginTop: 14,
    marginBottom: 0,
    fontSize: 12,
    color: PALETTE.textMuted,
    lineHeight: 1.5,
  },
};